import {Injectable} from "@angular/core";
import {environment} from "../../../environments/environment";
import {RequestData, RequestState, RequestStore} from "./request.store";
import {
  catchError,
  defer,
  EMPTY, expand,
  filter,
  finalize,
  first,
  forkJoin, last,
  map,
  Observable,
  switchMap,
  tap,
  timeout
} from "rxjs";
import {AuthService} from "@auth0/auth0-angular";
import {Request, RequestStatus} from "../../data-access/api-folders/Folder";
import {HttpClient, HttpParams} from "@angular/common/http";
import {arrayRemove, arrayUpsert} from "@datorama/akita";
import {PageResponse} from "../../data-access/PageResponse";
import {retryBackoff} from "../../data-access/retry.service";

@Injectable({ providedIn: 'root' })
export class RequestStoreService {
  private readonly baseUrl: string;
  private apiV1: string = environment.apiV1;
  private baseRef: string = environment.origin;
  private serviceCore: string = environment.service.core;

  constructor(
    private store: RequestStore,
    private httpClient: HttpClient,
    private authService: AuthService,
  ) {
    this.baseUrl = environment.local;
  }

  public setLoading(isLoading: boolean): void {
    this.store.setLoading(isLoading);
  }

  public init(): void {
    this.setLoading(true);

    this.authService.isLoading$
      .pipe(
        // wait until SDK finished init/callback
        filter((loading: boolean) => !loading),
        switchMap(() => this.authService.isAuthenticated$),
        first(),
        // safe: token is ready, interceptor will attach it
        switchMap((isAuth: boolean) => {
          if (!isAuth) {
            this.authService.loginWithRedirect({ appState: { target: '/services'} });
            return EMPTY;
          }
          return this.loadStatusesAndRequestsThenShadow$();
        }),
        finalize(() => this.setLoading(false)),
        catchError((err) => {
          console.error('[bootstrap requests] init failed', err);
          return EMPTY;
        })
      ).subscribe();
  }

  public loadStatusesAndRequestsThenShadow$(): Observable<void> {
    return forkJoin({
      requests: this.loadRequestsFirstPage$(50),
      requestStatuses: this.loadRequestStatuses$(),
    }).pipe(
      tap(() => {
        // shadow non bloquant
        this.loadRequestsShadowAll$(50).subscribe();
      }),
      map(() => void 0),
      tap(() => console.log('[bootstrap requests] all datasets loaded'))
    );
  }

  public loadRequestsFirstPage$(size: number): Observable<any> {
    return this.getRequestsPage({ page: 0, size, sort: 'dateUpd,desc' })
      .pipe(
        timeout({ first: 10000 }),
        retryBackoff(1, 400),
        tap((res: PageResponse<Request>) => {
          this.store.update((state: RequestState) => ({
            ...state,
            requests: this.mergeRequests(state.requests, res.items),
            requestPage: 1,
            requestHasNext: res.hasNext,
            error: undefined
          }));
        }),
        catchError(err => {
          this.store.setError(err);
          return EMPTY;
        })
      );
  }

  private loadRequestStatuses$(): Observable<any> {
    return this.fetchRequestStatuses()
      .pipe(
        tap((statuses: RequestStatus[]) => {
          this.store.update((state: RequestState) => ({
            ...state,
            requestStatuses: statuses,
            error: undefined
          }));
        }),
        catchError((error) => {
          this.store.setError(error);
          return EMPTY;
        })
      );
  }

  private loadRequestsShadowPage$(size: number): Observable<PageResponse<Request>> {
    const state: RequestData = this.store.getValue();
    if (!state.requestHasNext) return EMPTY;

    const page: number = state.requestPage;

    return this.getRequestsPage({ page, size, sort: 'dateUpd,desc' }).pipe(
      timeout({ first: 15000 }),
      retryBackoff(2, 500),
      tap((res: PageResponse<Request>) => {
        this.store.update((s: RequestState) => ({
          ...s,
          requests: this.mergeRequests(s.requests, res.items),
          requestPage: page + 1,
          requestHasNext: res.hasNext,
          error: undefined
        }));
      }),
      catchError(err => {
        this.store.setError(err);
        return EMPTY;
      })
    );
  }

  private loadRequestsShadowAll$(size: number): Observable<void> {
    return defer(() => this.loadRequestsShadowPage$(size)).pipe(
      expand(() => {
        const state: RequestData = this.store.getValue();
        return state.requestHasNext ? this.loadRequestsShadowPage$(size) : EMPTY;
      }),
      last(undefined, undefined),
      map(() => void 0)
    );
  }

  private mergeRequests(existing: Request[], incoming: Request[]): Request[] {
    const map = new Map<string, Request>();
    for (const r of existing) map.set(r.id, r);
    for (const r of incoming) map.set(r.id, r);
    return Array.from(map.values());
  }

  private getRequestsPage(params: { page?: number; size?: number; sort?: string }): Observable<PageResponse<Request>> {
    const url: string = `${this.baseRef}${this.serviceCore}${this.apiV1}requests/pageable`;

    const httpParams: HttpParams = new HttpParams()
      .set('page', String(params.page ?? 0))
      .set('size', String(params.size ?? 50))
      .set('sort', params.sort ?? 'dateUpd,desc');

    return this.httpClient.get<PageResponse<Request>>(url, { params: httpParams });
  }

  private fetchRequestStatuses = () => this.httpClient.get<RequestStatus[]>(
    `${this.baseRef}${this.serviceCore}${this.apiV1}folder/statuses`
  );

  /**
   * UPSERT AND DELETE METHODS
   *
   *
   *
   */

  public upsertRequest(request: Request): void {
    this.store.update((state: RequestState) => {
      const requests: Request[] = arrayUpsert(state.requests, request.id, request);
      return {
        ...state,
        requests
      };
    });
  }

  public async deleteRequest(requestId: string): Promise<boolean> {
    return new Promise<boolean>((resolve => {
      this.store.update((state: RequestState) => {
        const requests: Request[] = arrayRemove(state.requests, requestId);
        return {
          ...state,
          requests
        };
      });
      resolve(true);
    }));
  }

}
